/* eslint-disable react/require-default-props */
import React, {ReactNode, useEffect, useState} from 'react';
import {
  AccessibilityInfo,
  Pressable,
  View,
  ViewProps,
  ViewStyle,
} from 'react-native';

import testProperties from '/util/testing/testProperties';

import KeyboardFocusable from '../ExtKeyboard/KeyboardFocusable';

import {styles} from './Card.style';

interface CardProps extends ViewProps {
  children: ReactNode;
  hasBorder?: boolean;
  shadow?: boolean;
  style?: ViewStyle;
  fullWidth?: boolean;
  testID?: string;
  onPress?: (() => void) | null;
  isDashboard?: boolean;
}

export const Card: React.FC<CardProps> = ({
  children = null,
  hasBorder = true,
  shadow = false,
  style: styleProp = {},
  fullWidth = false,
  testID = null,
  onPress = null,
  isDashboard = false,
  ...rest
}) => {
  const style: ViewStyle[] = [styles.card];

  if (shadow) {
    style.push(styles.shadow);
  }

  if (hasBorder) {
    style.push(styles.border);
  }

  if (fullWidth) {
    style.push(styles.fullWidth);
  }

  if (styleProp) {
    style.push(styleProp);
  }

  const [isScreenReaderOn, setIsScreenReaderOn] = useState(false);
  useEffect(() => {
    AccessibilityInfo.isScreenReaderEnabled().then(setIsScreenReaderOn);

    const sub = AccessibilityInfo.addEventListener(
      'screenReaderChanged',
      setIsScreenReaderOn,
    );

    return () => sub.remove();
  }, []);

  if (!isDashboard) {
    const BaseComponent = !isScreenReaderOn ? KeyboardFocusable : View;
    return (
      <BaseComponent
        onPress={onPress || undefined}
        {...testProperties(null, true)}
        {...rest}>
        <View
          {...testProperties(testID, true)}
          style={style}
          accessible={false}>
          {children}
        </View>
      </BaseComponent>
    );
  }

  // DashboardMetricCard variant: children accessible + pressable overlay
  return (
    <View {...testProperties(testID, true)} style={style}>
      {React.Children.map(children, child =>
        React.isValidElement(child)
          ? React.cloneElement(child, {
              accessible: true,
            })
          : child,
      )}

      {onPress && (
        <Pressable
          testID="pressable-card"
          onPress={onPress}
          style={{position: 'absolute', top: 0, left: 0, right: 50, bottom: 0}}
          pointerEvents="box-only"
          accessible={false}
        />
      )}
    </View>
  );
};

export default Card;
